import type { ReactNode } from "react";

const panelClass =
  "relative w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800 dark:shadow-black/40";

const headerClass =
  "border-b border-slate-100 px-5 py-4 dark:border-slate-700";

const titleClass = "text-base font-semibold text-slate-900 dark:text-slate-100";

const descriptionClass = "mt-1 text-sm text-slate-600 dark:text-slate-400";

const footerClass =
  "flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3 dark:border-slate-700";

const overlayClass = "absolute inset-0 bg-black/40 dark:bg-black/60";

export const adminModalInputClass =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

export const adminModalLabelClass =
  "mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300";

export const adminModalFileInputClass =
  "block w-full text-sm text-slate-700 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-800 dark:text-slate-300 dark:file:bg-slate-700 dark:file:text-slate-100";

type AdminModalProps = {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer: ReactNode;
  maxWidth?: "lg" | "2xl";
  bodyClassName?: string;
};

export function AdminModal({
  open,
  onClose,
  ariaLabel,
  title,
  description,
  children,
  footer,
  maxWidth = "lg",
  bodyClassName = "px-5 py-4",
}: AdminModalProps) {
  const widthClass = maxWidth === "2xl" ? "max-w-2xl" : "max-w-lg";

  return (
    <div
      className={[
        open ? "fixed" : "hidden",
        "inset-0 z-50 flex items-center justify-center p-4",
      ].join(" ")}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={overlayClass} aria-hidden />

      <div className={[panelClass, widthClass, "flex max-h-[90vh] flex-col"].join(" ")}>
        <div className={headerClass}>
          <div className={titleClass}>{title}</div>
          {description ? (
            <div className={descriptionClass}>{description}</div>
          ) : null}
        </div>

        {children ? (
          <div className={[bodyClassName, "overflow-y-auto"].join(" ")}>
            {children}
          </div>
        ) : null}

        <div className={footerClass}>{footer}</div>
      </div>
    </div>
  );
}
